import React, { useMemo, useRef, type RefObject } from 'react';
import {
  Box,
  Button,
  Chip,
  Stack,
  useTheme,
} from '@mui/material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { borderRadius, getModernScrollbar, layoutGap } from '../../../theme/theme';
import type { ValidationCheckResult } from '../types';
import type { ValidationIssueFamily } from '../lib/validationIssueFamilies';
import ValidationIssueCard from './ValidationIssueCard';
import ValidationPriorityBadge from './ValidationPriorityBadge';
import VirtualizedList from '../../../shared/ui/workflow/VirtualizedList';
import WorkspaceEmptyState from '../../../shared/ui/workflow/WorkspaceEmptyState';
import ActionHelpTooltip from '../../../shared/ui/common/ActionHelpTooltip';
import { validationActionTooltips } from '../../../shared/config/actionHelpCatalog';
import {
  resolveCanonicalValidationTitle,
  resolveVisibleValidationNumber,
} from '../validationConstants';
import {
  WorkspaceCallout,
  WorkspacePaneHeader,
  WorkspaceStatusPill,
  getWorkflowWorkspacePalette,
} from '../../../shared/ui/workflow/WorkflowWorkspacePrimitives';
import { validationCopy } from '../copy/validationCopy';
import { useValidationQueueKeyboard } from '../hooks/useValidationQueueKeyboard';
import { useValidationRepairQueueHint } from '../hooks/useValidationRepairQueueHint';
import { BodyText, CaptionText, CardTitle } from '../../../shared/ui/typography';

interface ValidationIssueListProps {
  families: ValidationIssueFamily[];
  displayNumbersByFamilyId: Map<string, number>;
  selectedFamilyId: string | null;
  onSelectFamily: (family: ValidationIssueFamily) => void;
  onExecutePrimaryAction: (family: ValidationIssueFamily) => void;
  showPassedOnly: boolean;
  passedChecks: ValidationCheckResult[];
  hasActiveFilters: boolean;
  onShowPassedOnly: () => void;
  tourRefs?: {
    selectedCardRef?: RefObject<HTMLDivElement | null>;
    selectedCardActionRef?: RefObject<HTMLDivElement | null>;
  };
}

const FAMILY_ROW_HEIGHT = 104;
const PASSED_ROW_HEIGHT = 96;
const VIRTUALIZATION_THRESHOLD = 200;

const QUEUE_SEVERITY_META = [
  { key: 'critical', label: 'Critical', pluralLabel: 'Critical', tone: 'error' },
  { key: 'error', label: 'Error', pluralLabel: 'Errors', tone: 'error' },
  { key: 'warning', label: 'Warning', pluralLabel: 'Warnings', tone: 'warning' },
] as const;

type QueueSeverityKey = (typeof QUEUE_SEVERITY_META)[number]['key'];

function isQueueSummarySeverity(severity: ValidationIssueFamily['severity']): severity is QueueSeverityKey {
  return severity === 'critical' || severity === 'error' || severity === 'warning';
}

const QueueSeveritySummary = React.memo(function QueueSeveritySummary({
  families,
}: {
  families: ValidationIssueFamily[];
}) {
  const counts = useMemo(() => {
    const result: Record<QueueSeverityKey, number> = { critical: 0, error: 0, warning: 0 };
    families.forEach((family) => {
      if (isQueueSummarySeverity(family.severity)) {
        result[family.severity] += 1;
      }
    });
    return result;
  }, [families]);

  return (
    <Stack
      direction="row"
      spacing={layoutGap.dense}
      alignItems="center"
      flexWrap="wrap"
      useFlexGap
      component="span"
      data-testid="validation-queue-severity-summary"
      sx={{ display: 'inline-flex' }}
    >
      {QUEUE_SEVERITY_META.filter(({ key }) => counts[key] > 0).map(({ key, label, pluralLabel, tone }) => (
        <WorkspaceStatusPill
          key={key}
          size="compact"
          tone={tone}
          dataTestId={`validation-queue-severity-${key}`}
          label={`${counts[key]} ${counts[key] === 1 ? label : pluralLabel}`}
        />
      ))}
    </Stack>
  );
});

const PassedCheckCard = React.memo(function PassedCheckCard({ check }: { check: ValidationCheckResult }) {
  const theme = useTheme();
  const displayNumber = resolveVisibleValidationNumber(check.ValidationId);
  const title = resolveCanonicalValidationTitle(check.ValidationId, check.Title);

  return (
    <Box
      sx={{
        p: 1.1,
        borderRadius: `${borderRadius.md}px`,
        border: `1px solid ${alpha(theme.palette.success.main, 0.18)}`,
        backgroundColor: alpha(theme.palette.success.main, 0.06),
      }}
    >
      <Stack spacing={layoutGap.cozy}>
        <Stack direction="row" spacing={layoutGap.comfort} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip
            icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
            label={validationCopy.queue.passedCard.badge}
            size="small"
            color="success"
            variant="outlined"
          />
          <ValidationPriorityBadge validationIds={check.ValidationId} compact showRank={false} />
        </Stack>
        <CardTitle
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {validationCopy.queue.passedCard.validationLabel(displayNumber, title)}
        </CardTitle>
        <BodyText
          secondary
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {check.Description}
        </BodyText>
      </Stack>
    </Box>
  );
});

export default function ValidationIssueList({
  families,
  displayNumbersByFamilyId,
  selectedFamilyId,
  onSelectFamily,
  onExecutePrimaryAction,
  showPassedOnly,
  passedChecks,
  hasActiveFilters,
  onShowPassedOnly,
  tourRefs,
}: ValidationIssueListProps) {
  const theme = useTheme();
  const workspacePalette = getWorkflowWorkspacePalette(theme);
  const { repairQueueHintDismissed, dismissRepairQueueHint } = useValidationRepairQueueHint();
  const { moveSelection, setRowRef } = useValidationQueueKeyboard(families, onSelectFamily);
  const useVirtualizedFamilies = families.length > VIRTUALIZATION_THRESHOLD;
  const useVirtualizedPassedChecks = showPassedOnly && passedChecks.length > VIRTUALIZATION_THRESHOLD;
  const tourFamilyId = selectedFamilyId ?? families[0]?.id ?? null;
  const passedListRef = useRef<HTMLDivElement | null>(null);

  const handleSetCardRef = (familyId: string, node: HTMLDivElement | null) => {
    setRowRef(familyId, node);
    if (familyId === tourFamilyId && tourRefs?.selectedCardRef) {
      tourRefs.selectedCardRef.current = node;
    }
  };

  const renderFamilyCard = (family: ValidationIssueFamily) => (
    <ValidationIssueCard
      ref={(node) => handleSetCardRef(family.id, node)}
      key={family.id}
      family={family}
      displayNumber={
        displayNumbersByFamilyId.get(family.id) ?? resolveVisibleValidationNumber(family.validationIds)
      }
      selected={family.id === selectedFamilyId}
      onSelect={() => onSelectFamily(family)}
      onExecutePrimaryAction={onExecutePrimaryAction}
      onMoveSelection={moveSelection}
      tourCardTestId={
        family.id === tourFamilyId
          ? 'validation-tour-target-queue-selected-card'
          : undefined
      }
      tourActionTestId={
        family.id === tourFamilyId
          ? 'validation-tour-target-queue-selected-card-action'
          : undefined
      }
      tourRefs={
        family.id === tourFamilyId
          ? {
              actionRef: tourRefs?.selectedCardActionRef,
            }
          : undefined
      }
    />
  );

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        borderRight: { xl: `1px solid ${alpha(theme.palette.divider, 0.12)}` },
      }}
    >
      <Box
        sx={{
          px: { xs: 1.35, md: 1.65 },
          py: 1,
          borderBottom: `1px solid ${workspacePalette.paneDivider}`,
          backgroundColor: workspacePalette.sectionRaisedBackground,
        }}
      >
        <WorkspacePaneHeader
          title={showPassedOnly ? validationCopy.queue.title.passedChecks : validationCopy.queue.title.issuesToFix}
          description={
            showPassedOnly ? (
              validationCopy.queue.description.passedCount(passedChecks.length)
            ) : families.length > 0 ? (
              <Stack direction="row" spacing={layoutGap.comfort} alignItems="center" flexWrap="wrap" useFlexGap>
                <CaptionText component="span" color="text.secondary">
                  {validationCopy.queue.description.issuesFound(families.length)}
                </CaptionText>
                <QueueSeveritySummary families={families} />
              </Stack>
            ) : (
              validationCopy.queue.description.noMatch
            )
          }
          sx={{ px: 0, py: 0, borderBottom: 'none' }}
        />
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          px: { xs: 1.05, md: 1.15 },
          py: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Stack spacing={layoutGap.row}>
          {!showPassedOnly &&
            families.length > 0 &&
            !selectedFamilyId &&
            !repairQueueHintDismissed && (
              <WorkspaceCallout
                title={validationCopy.queue.callout.gettingStarted.title}
                description={validationCopy.queue.callout.gettingStarted.description}
                tone="info"
                actions={
                  <Button
                    size="small"
                    variant="text"
                    onClick={dismissRepairQueueHint}
                    sx={{ alignSelf: 'flex-start', px: 0 }}
                  >
                    {validationCopy.queue.callout.gettingStarted.action}
                  </Button>
                }
              />
            )}
          {showPassedOnly && passedChecks.length === 0 && (
            <WorkspaceEmptyState
              title={validationCopy.queue.description.noPassedMatch}
              tone="info"
            />
          )}

          {!showPassedOnly && families.length === 0 && hasActiveFilters && (
            <WorkspaceEmptyState
              title={validationCopy.queue.description.noUnresolvedMatch}
              tone="info"
            />
          )}

          {!showPassedOnly && families.length === 0 && !hasActiveFilters && passedChecks.length > 0 && (
            <WorkspaceCallout
              title={validationCopy.queue.callout.noUnresolvedIssues.title}
              description={validationCopy.queue.callout.noUnresolvedIssues.description}
              tone="success"
              actions={
                <Stack spacing={layoutGap.plate}>
                  <Stack direction="row" spacing={layoutGap.comfort} flexWrap="wrap" useFlexGap>
                    <Chip
                      label={validationCopy.queue.callout.noUnresolvedIssues.passedChecksChip(passedChecks.length)}
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  </Stack>
                  <ActionHelpTooltip help={validationActionTooltips.reviewPassedChecks}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={onShowPassedOnly}
                      sx={{ alignSelf: 'flex-start' }}
                    >
                      {validationCopy.queue.callout.noUnresolvedIssues.action}
                    </Button>
                  </ActionHelpTooltip>
                </Stack>
              }
            />
          )}
        </Stack>

        {showPassedOnly && passedChecks.length > 0 && (
          useVirtualizedPassedChecks ? (
            <Box
              ref={passedListRef}
              role="listbox"
              aria-label={validationCopy.queue.ariaLabel}
              data-testid="validation-repair-queue-virtualized"
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <VirtualizedList
                items={passedChecks}
                itemHeight={PASSED_ROW_HEIGHT}
                getKey={(check) => check.ValidationId}
                renderItem={(check) => <PassedCheckCard key={check.ValidationId} check={check} />}
                role="listbox"
                ariaLabel={validationCopy.queue.ariaLabel}
              />
            </Box>
          ) : (
            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pb: 0.5, ...getModernScrollbar(theme) }}>
              <Stack spacing={layoutGap.flow}>
                {passedChecks.map((check) => <PassedCheckCard key={check.ValidationId} check={check} />)}
              </Stack>
            </Box>
          )
        )}

        {!showPassedOnly && families.length > 0 && (
          useVirtualizedFamilies ? (
            <Box
              role="listbox"
              aria-label={validationCopy.queue.ariaLabel}
              data-testid="validation-repair-queue-virtualized"
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <VirtualizedList
                items={families}
                itemHeight={FAMILY_ROW_HEIGHT}
                getKey={(family) => family.id}
                renderItem={(family) => renderFamilyCard(family)}
                role="listbox"
                ariaLabel={validationCopy.queue.ariaLabel}
              />
            </Box>
          ) : (
            <Box
              role="listbox"
              aria-label={validationCopy.queue.ariaLabel}
              sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pb: 0.5, ...getModernScrollbar(theme) }}
            >
              <Stack spacing={layoutGap.snug}>
                {families.map((family) => renderFamilyCard(family))}
              </Stack>
            </Box>
          )
        )}
      </Box>
    </Box>
  );
}
